import React, { useState, useRef, useEffect } from 'react';
import { db, storage, auth } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { userService } from './services/userService';
import { API_BASE, getAuthHeader } from './services/api';
import LimitReachedModal from './LimitReachedModal';
import ManualInputForm from './ManualInputForm';

const CardScanner = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cameraFileName] = useState('Take a Picture');
  const [showInstruction, setShowInstruction] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [user, setUser] = useState(null);
  const [editingParsedData, setEditingParsedData] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [showLimitReached, setShowLimitReached] = useState(false);
  const [remainingScans, setRemainingScans] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchResults, setBatchResults] = useState([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const fileInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const batchInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const scanInfo = await userService.getRemainingScans(currentUser.uid, currentUser.email);
        setRemainingScans(scanInfo);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleImageCapture = async (e) => {
    console.log('handleImageCapture triggered');
    const file = e.target.files[0];
    if (file && user) {
      // 檢查掃描限制
      const canScanResult = await userService.canScan(user.uid, user.email);
      if (!canScanResult.allowed) {
        setShowLimitReached(true);
        // 重置文件輸入
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        return;
      }
      
      setUploading(true);
      setShowInstruction(false);
      
      try {
        // 先直接發送檔案到後端處理
        await handleFileUpload(file);
      } catch (error) {
        console.error('檔案處理失敗:', error);
        alert('檔案處理失敗，請重試');
        setUploading(false);
      }
    }
  };

  const handleImageUpload = async (e) => {
    console.log('handleImageUpload triggered');
    const file = e.target.files[0];
    if (file && user) {
      // 檢查掃描限制
      const canScanResult = await userService.canScan(user.uid, user.email);
      if (!canScanResult.allowed) {
        setShowLimitReached(true);
        // 重置文件輸入
        if (uploadInputRef.current) {
          uploadInputRef.current.value = null;
        }
        return;
      }
      setUploading(true);
      setShowInstruction(false);
      
      try {
        // 先直接發送檔案到後端處理
        await handleFileUpload(file);
      } catch (error) {
        console.error('檔案處理失敗:', error);
        alert('檔案處理失敗，請重試');
        setUploading(false);
      }
    }
  };

  const handleFileUpload = async (file) => {
    try {
      console.log('發送檔案到後端處理:', file.name, 'type:', file.type);
      
      // 檢查檔案類型並給出提示
      if (file.name.toLowerCase().includes('.heic') || file.type.includes('heic')) {
        console.log('檢測到 HEIC 格式檔案，正在處理...');
      }
      
      const formData = new FormData();
      formData.append('image', file);
      
      setLoading(true);
      
      const authHeader = await getAuthHeader();
      const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { ...authHeader },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`檔案上傳失敗: ${response.status}`);
      }

      const data = await response.json();
      console.log('後端處理結果:', data);

      if (data.parsedData) {
        setParsedData(data.parsedData);
        
        // 如果後端返回了處理後的圖片，設為預覽圖片
        if (data.processedImage) {
          setImageUrl(data.processedImage);
        } else {
          // 否則創建本地預覽URL
          const localUrl = URL.createObjectURL(file);
          setImageUrl(localUrl);
        }
        
        // 然後上傳到Firebase存儲（用於記錄保存）
        await uploadToFirebase(file);
      } else {
        throw new Error('後端回應格式錯誤');
      }

      setUploading(false);
      setLoading(false);
    } catch (error) {
      console.error('檔案上傳失敗:', error);
      setUploading(false);
      setLoading(false);
      
      let errorMessage = '檔案處理失敗';
      if (error.message.includes('HEIC轉換失敗') || error.message.includes('HEIC')) {
        errorMessage = 'iPhone 照片格式處理失敗，請嘗試在iPhone設定中將相機格式改為「最兼容」或重新拍照';
      } else if (error.message.includes('authentication')) {
        errorMessage = 'API認證失敗，請檢查服務設定';
      } else if (error.message.includes('500')) {
        errorMessage = '服務器內部錯誤，請稍後再試';
      } else if (error.message.includes('415')) {
        errorMessage = '不支援的檔案格式，請使用 JPG、PNG 或標準相機格式';
      } else {
        errorMessage = `處理失敗: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const uploadToFirebase = async (file) => {
    try {
      const timestamp = new Date().getTime();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `imagesgroup/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setImageUrl(url); // 更新為Firebase URL供儲存使用
      console.log('Firebase上傳完成:', url);
    } catch (error) {
      console.error('Firebase上傳失敗:', error);
      // 不需要顯示錯誤，因為主要功能已完成
    }
  };



  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  const handleEditParsedData = () => {
    setEditingParsedData(true);
    setEditedData({ ...parsedData });
  };

  const handleSaveParsedData = () => {
    setParsedData(editedData);
    setEditingParsedData(false);
  };

  const handleCancelEditParsedData = () => {
    setEditingParsedData(false);
    setEditedData({});
  };

  const handleFieldChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleConfirmUpload = async () => {
    if (parsedData && user) {
      const dataWithNote = { 
        ...parsedData, 
        note,
        imageUrl,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email
      };
      await saveDataToFirebase(dataWithNote);
      
      // 增加掃描次数
      await userService.incrementScanCount(user.uid);
      
      // 更新剩餘次数
      const scanInfo = await userService.getRemainingScans(user.uid, user.email);
      setRemainingScans(scanInfo);
      
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        resetForm();
      }, 3000);
    }
  };

  const saveDataToFirebase = async (data) => {
    try {
      await addDoc(collection(db, 'businessCardsnotenew'), {
        ...data,
        timestamp: serverTimestamp(),
      });
      console.log('Document successfully written!');
    } catch (error) {
      console.error('Error writing document:', error);
    }
  };

  const resetForm = () => {
    setImageUrl('');
    setParsedData(null);
    setNote('');
    setEditingParsedData(false);
    setEditedData({});
    setShowInstruction(true);
    resetBatchResults();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (uploadInputRef.current) {
      uploadInputRef.current.value = null;
    }
  };

  const handleCloseLimitModal = () => {
    setShowLimitReached(false);
  };

  const handleOpenManualInput = () => {
    setShowManualInput(true);
  };

  const handleCloseManualInput = () => {
    setShowManualInput(false);
  };

  const handleManualInputSuccess = async () => {
    // 更新剩餘次數
    if (user) {
      const scanInfo = await userService.getRemainingScans(user.uid, user.email);
      setRemainingScans(scanInfo);
    }
    
    // 顯示成功訊息
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const handleBatchUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0 || !user) return;

    // 檢查掃描限制
    const canScanResult = await userService.canScan(user.uid, user.email);
    if (!canScanResult.allowed) {
      setShowLimitReached(true);
      if (batchInputRef.current) {
        batchInputRef.current.value = null;
      }
      return;
    }

    // 檢查是否有足夠的剩餘次數
    if (!userService.isPremiumUser(user.email)) {
      const userData = await userService.getUserData(user.uid);
      const remaining = 10 - userData.scanCount;
      if (files.length > remaining) {
        alert(`您的剩餘掃描次數不足（還剩 ${remaining} 次），無法一次處理 ${files.length} 張名片。`);
        if (batchInputRef.current) {
          batchInputRef.current.value = null;
        }
        return;
      }
    }

    setBatchProcessing(true);
    setBatchResults([]);
    setCurrentBatchIndex(0);
    setShowInstruction(false);

    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      setCurrentBatchIndex(i + 1);
      
      try {
        const result = await processSingleFile(files[i], i);
        results.push(result);
      } catch (error) {
        console.error(`處理第 ${i + 1} 張名片失敗:`, error);
        results.push({
          index: i + 1,
          filename: files[i].name,
          error: error.message,
          success: false
        });
      }
    }

    setBatchResults(results);
    setBatchProcessing(false);
    
    // 更新剩餘次數
    const scanInfo = await userService.getRemainingScans(user.uid, user.email);
    setRemainingScans(scanInfo);
  };

  const processSingleFile = async (file, index) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const authHeader = await getAuthHeader();
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: { ...authHeader },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`檔案上傳失敗: ${response.status}`);
    }

    const data = await response.json();

    if (data.parsedData) {
      // 先把圖片上傳到 Storage、只存短網址；避免把整張 base64 塞進 Firestore 超過 1 MiB 單文件上限（批次會存不進去）
      let imageUrl = '';
      try {
        const timestamp = new Date().getTime();
        const fileName = `${timestamp}_${index}_${file.name}`;
        const storageRef = ref(storage, `imagesgroup/${fileName}`);
        await uploadBytes(storageRef, file);
        imageUrl = await getDownloadURL(storageRef);
      } catch (storageError) {
        console.error(`批次第 ${index + 1} 張圖片上傳 Storage 失敗:`, storageError);
      }

      // 直接儲存到 Firebase
      const dataToSave = {
        ...data.parsedData,
        note: `批量掃描 - 第 ${index + 1} 張`,
        imageUrl,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email,
        isBatchScan: true,
        batchIndex: index + 1
      };

      await saveDataToFirebase(dataToSave);
      await userService.incrementScanCount(user.uid);

      return {
        index: index + 1,
        filename: file.name,
        parsedData: data.parsedData,
        success: true
      };
    } else {
      throw new Error('後端回應格式錯誤');
    }
  };

  const resetBatchResults = () => {
    setBatchResults([]);
    setBatchProcessing(false);
    setCurrentBatchIndex(0);
    setShowInstruction(true);
    if (batchInputRef.current) {
      batchInputRef.current.value = null;
    }
  };

  return (
    <div className="card-scanner-container">
      {showInstruction && <div className="initial-instruction">Please tap the camera button to take a picture or upload button to select from gallery.</div>}
      
      {/* 顯示剩餘掃描次数 */}
      {remainingScans && !remainingScans.unlimited && (
        <div className="scan-counter">
          剩餘掃描次数: {remainingScans.remaining}/{remainingScans.total}
        </div>
      )}
      
      <div className="button-container">
        <input
          type="file"
          accept="image/*,.heic,.HEIC"
          capture="environment"
          onChange={handleImageCapture}
          disabled={uploading || loading}
          style={{ display: 'none' }}
          id="cameraInput"
          ref={fileInputRef}
        />
        <label htmlFor="cameraInput" className="custom-file-input camera-input">
          {cameraFileName}
        </label>
        
        <input
          type="file"
          accept="image/*,.heic,.HEIC"
          onChange={handleImageUpload}
          disabled={uploading || loading}
          style={{ display: 'none' }}
          id="uploadInput"
          ref={uploadInputRef}
        />
        <label htmlFor="uploadInput" className="custom-file-input upload-input">
          Upload Photo
        </label>
        
        <button onClick={handleOpenManualInput} className="custom-file-input manual-input">
          手動輸入
        </button>
        
        <input
          type="file"
          accept="image/*,.heic,.HEIC"
          multiple
          onChange={handleBatchUpload}
          disabled={uploading || loading}
          style={{ display: 'none' }}
          id="batchInput"
          ref={batchInputRef}
        />
        <label htmlFor="batchInput" className="custom-file-input batch-input">
          批量掃描
        </label>
      </div>
      {uploading && <div className="uploading-overlay">Uploading...</div>}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <span className="dot-flashing"></span>
            <span>Processing...</span>
          </div>
        </div>
      )}
      
      {batchProcessing && (
        <div className="loading-overlay">
          <div className="loading-content">
            <span className="dot-flashing"></span>
            <span>批量處理中... ({currentBatchIndex}/總數)</span>
          </div>
        </div>
      )}
      {showSuccessMessage && (
        <div className="success-overlay">
          <div className="success-content">
            <span>Upload Successful!</span>
          </div>
        </div>
      )}
      
      {/* 限制提示模態框 */}
      {showLimitReached && (
        <LimitReachedModal onClose={handleCloseLimitModal} />
      )}
      
      {/* 手動輸入模態框 */}
      {showManualInput && (
        <ManualInputForm 
          onClose={handleCloseManualInput}
          onSuccess={handleManualInputSuccess}
        />
      )}
      
      <div>
        {imageUrl && (
          <div className="image-preview">
            <h3>📷 拍攝的名片</h3>
            <img src={imageUrl} alt="Uploaded business card" className="card-image" />
          </div>
        )}
        {parsedData && (
          <div className="parsed-data">
            <div className="parsed-data-header">
              <h3>📋 解析結果</h3>
              {!editingParsedData && (
                <button onClick={handleEditParsedData} className="edit-parsed-btn">
                  ✏️ 編輯
                </button>
              )}
            </div>
            
            {editingParsedData ? (
              <div className="edit-parsed-form">
                <div className="data-display">
                  {Object.entries(editedData).map(([key, value]) => (
                    <div key={key} className="edit-data-field">
                      <label>{key}:</label>
                      <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        className="edit-data-input"
                      />
                    </div>
                  ))}
                </div>
                <div className="edit-actions">
                  <button onClick={handleCancelEditParsedData} className="cancel-edit-btn">取消</button>
                  <button onClick={handleSaveParsedData} className="save-edit-btn">保存</button>
                </div>
              </div>
            ) : (
              <div className="data-display">
                {Object.entries(parsedData).map(([key, value]) => (
                  <div key={key} className="data-field">
                    <strong>{key}:</strong> <span>{value}</span>
                  </div>
                ))}
              </div>
            )}
            
            <div className="note-section">
              <label>💭 備註 (合作意向或下一步行動):</label>
              <textarea
                placeholder="例如：對其產品有興趣，下週安排會議討論合作可能性..."
                value={note}
                onChange={handleNoteChange}
                className="note-textarea"
              />
            </div>
            <button onClick={handleConfirmUpload} className="upload-button" disabled={editingParsedData}>
              {editingParsedData ? '請先完成編輯' : '確認儲存'}
            </button>
          </div>
        )}
        
        {/* 批量處理結果 */}
        {batchResults.length > 0 && (
          <div className="batch-results">
            <div className="batch-results-header">
              <h3>📊 批量處理結果</h3>
              <button onClick={resetBatchResults} className="clear-results-btn">
                清除結果
              </button>
            </div>
            
            <div className="batch-summary">
              <div className="summary-item success">
                <span>成功: {batchResults.filter(r => r.success).length}</span>
              </div>
              <div className="summary-item error">
                <span>失敗: {batchResults.filter(r => !r.success).length}</span>
              </div>
              <div className="summary-item total">
                <span>總計: {batchResults.length}</span>
              </div>
            </div>
            
            <div className="batch-results-list">
              {batchResults.map((result, index) => (
                <div key={index} className={`batch-result-item ${result.success ? 'success' : 'error'}`}>
                  <div className="result-header">
                    <span className="result-index">第 {result.index} 張</span>
                    <span className="result-filename">{result.filename}</span>
                    <span className={`result-status ${result.success ? 'success' : 'error'}`}>
                      {result.success ? '✓ 成功' : '✗ 失敗'}
                    </span>
                  </div>
                  
                  {result.success && result.parsedData && (
                    <div className="result-data">
                      {Object.entries(result.parsedData).map(([key, value]) => (
                        <div key={key} className="result-field">
                          <strong>{key}:</strong> <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {result.error && (
                    <div className="result-error">
                      錯誤: {result.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="batch-actions">
              <button onClick={() => {
                setShowSuccessMessage(true);
                setTimeout(() => {
                  setShowSuccessMessage(false);
                  resetBatchResults();
                }, 3000);
              }} className="finish-batch-btn">
                完成批量處理
              </button>
            </div>
          </div>
        )}
      </div>
      <style jsx="true">{`
        .card-scanner-container {
          padding: 30px;
          min-height: calc(100vh - 120px);
          background: transparent;
          position: relative;
        }

        .scan-counter {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 20px;
          padding: 16px 24px;
          margin: 20px 0;
          text-align: center;
          color: rgba(108, 99, 143, 0.9);
          font-weight: 600;
          font-size: 16px;
          box-shadow: 
            0 4px 16px rgba(31, 38, 135, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .uploading-overlay, .loading-overlay, .success-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(102, 126, 234, 0.9);
          backdrop-filter: blur(10px);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 24px;
          z-index: 1000;
        }

        .loading-content, .success-content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .dot-flashing {
          position: relative;
          width: 1em;
          height: 1em;
          margin: 0.5em 0;
          border-radius: 50%;
          background: white;
          color: white;
          animation: dotFlashing 1s infinite linear alternate;
          animation-delay: 0.5s;
        }
        
        .dot-flashing::before, .dot-flashing::after {
          content: '';
          display: inline-block;
          position: absolute;
          top: 0;
        }
        
        .dot-flashing::before {
          left: -1.5em;
          width: 1em;
          height: 1em;
          border-radius: 50%;
          background: white;
          color: white;
          animation: dotFlashing 1s infinite alternate;
          animation-delay: 0s;
        }
        
        .dot-flashing::after {
          left: 1.5em;
          width: 1em;
          height: 1em;
          border-radius: 50%;
          background: white;
          color: white;
          animation: dotFlashing 1s infinite alternate;
          animation-delay: 1s;
        }

        @keyframes dotFlashing {
          0% {
            background-color: white;
          }
          50%,
          100% {
            background-color: rgba(255, 255, 255, 0.2);
          }
        }


        .initial-instruction {
          margin: 30px 0;
          padding: 30px;
          font-size: 18px;
          font-weight: 500;
          color: rgba(108, 99, 143, 0.9);
          text-align: center;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .button-container {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
          margin: 25px 0;
        }

        .manual-input {
          border: none;
          background: none;
        }

        .custom-file-input {
          display: inline-block;
          padding: 18px 40px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          color: rgba(108, 99, 143, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 60px;
          margin: 25px 0;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          text-transform: none;
          letter-spacing: 0.5px;
          position: relative;
          overflow: hidden;
        }

        .camera-input::before {
          content: '📷';
          margin-right: 8px;
          font-size: 20px;
        }

        .upload-input::before {
          content: '📁';
          margin-right: 8px;
          font-size: 20px;
        }

        .manual-input::before {
          content: '✏️';
          margin-right: 8px;
          font-size: 20px;
        }

        .batch-input::before {
          content: '📊';
          margin-right: 8px;
          font-size: 20px;
        }

        .custom-file-input:hover {
          transform: translateY(-3px);
          background: rgba(255, 255, 255, 0.35);
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 12px 40px rgba(31, 38, 135, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .custom-file-input:active {
          transform: translateY(-1px);
        }

        .upload-button {
          display: inline-block;
          padding: 14px 28px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          border: none;
          border-radius: 25px;
          margin-top: 15px;
          box-shadow: 0 6px 15px rgba(86, 171, 47, 0.3);
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .upload-button::before {
          content: '✓';
          margin-right: 6px;
          font-size: 16px;
        }

        .upload-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(86, 171, 47, 0.4);
        }

        .success-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 24px;
          z-index: 1000;
        }

        .success-content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .image-preview {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          padding: 24px;
          margin: 25px 0;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
          text-align: center;
        }

        .image-preview h3 {
          color: rgba(108, 99, 143, 0.9);
          margin-bottom: 20px;
          font-size: 18px;
          font-weight: 600;
        }

        .card-image {
          max-width: 100%;
          max-height: 300px;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .parsed-data {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          padding: 28px;
          margin: 25px 0;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .parsed-data-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .parsed-data h3 {
          color: rgba(108, 99, 143, 0.9);
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .edit-parsed-btn {
          background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
          transition: all 0.3s ease;
        }

        .edit-parsed-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(255, 193, 7, 0.4);
        }

        .edit-data-field {
          margin-bottom: 16px;
        }

        .edit-data-field label {
          display: block;
          margin-bottom: 6px;
          color: rgba(108, 99, 143, 0.8);
          font-weight: 600;
          font-size: 14px;
        }

        .edit-data-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          color: rgba(108, 99, 143, 0.9);
          transition: all 0.3s ease;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
          box-sizing: border-box;
        }

        .edit-data-input:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.5);
          border-color: rgba(255, 255, 255, 0.7);
          box-shadow: 
            0 0 0 3px rgba(63, 81, 181, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .edit-actions {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
          justify-content: center;
        }

        .cancel-edit-btn {
          background: linear-gradient(135deg, #6c757d 0%, #adb5bd 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        }

        .cancel-edit-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(108, 117, 125, 0.4);
        }

        .save-edit-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(86, 171, 47, 0.3);
        }

        .save-edit-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(86, 171, 47, 0.4);
        }

        .upload-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }

        .data-display {
          margin-bottom: 24px;
        }

        .data-field {
          padding: 14px 18px;
          margin: 10px 0;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 12px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .data-field strong {
          color: rgba(108, 99, 143, 0.8);
          margin-right: 10px;
          font-weight: 600;
        }

        .data-field span {
          color: rgba(108, 99, 143, 0.9);
        }

        .note-section {
          margin-bottom: 20px;
        }

        .note-section label {
          display: block;
          color: rgba(108, 99, 143, 0.8);
          font-weight: 600;
          margin-bottom: 12px;
          font-size: 15px;
        }

        .note-textarea {
          width: 100%;
          min-height: 100px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          font-size: 14px;
          font-family: inherit;
          color: rgba(108, 99, 143, 0.9);
          resize: vertical;
          transition: all 0.3s ease;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
        }

        .note-textarea:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.4);
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 0 0 3px rgba(63, 81, 181, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .batch-results {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          padding: 24px;
          margin: 25px 0;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .batch-results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .batch-results-header h3 {
          color: rgba(108, 99, 143, 0.9);
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .clear-results-btn {
          background: rgba(255, 107, 107, 0.3);
          border: 1px solid rgba(255, 107, 107, 0.4);
          color: rgba(180, 60, 60, 0.9);
          padding: 8px 16px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .clear-results-btn:hover {
          background: rgba(255, 107, 107, 0.4);
          transform: translateY(-1px);
        }

        .batch-summary {
          display: flex;
          gap: 16px;
          margin-bottom: 20px;
          justify-content: center;
        }

        .summary-item {
          padding: 12px 20px;
          border-radius: 16px;
          text-align: center;
          font-weight: 600;
          font-size: 14px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .summary-item.success {
          background: rgba(76, 175, 80, 0.2);
          color: rgba(46, 125, 50, 0.9);
        }

        .summary-item.error {
          background: rgba(244, 67, 54, 0.2);
          color: rgba(198, 40, 40, 0.9);
        }

        .summary-item.total {
          background: rgba(102, 126, 234, 0.2);
          color: rgba(63, 81, 181, 0.9);
        }

        .batch-results-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
          max-height: 400px;
          overflow-y: auto;
        }

        .batch-result-item {
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          padding: 16px;
          transition: all 0.2s ease;
        }

        .batch-result-item:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .batch-result-item.success {
          border-left: 4px solid rgba(76, 175, 80, 0.8);
        }

        .batch-result-item.error {
          border-left: 4px solid rgba(244, 67, 54, 0.8);
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .result-index {
          font-weight: 600;
          color: rgba(108, 99, 143, 0.9);
          font-size: 14px;
        }

        .result-filename {
          flex: 1;
          margin: 0 12px;
          font-size: 13px;
          color: rgba(108, 99, 143, 0.7);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .result-status {
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
        }

        .result-status.success {
          background: rgba(76, 175, 80, 0.2);
          color: rgba(46, 125, 50, 0.9);
        }

        .result-status.error {
          background: rgba(244, 67, 54, 0.2);
          color: rgba(198, 40, 40, 0.9);
        }

        .result-data {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
        }

        .result-field {
          font-size: 12px;
          color: rgba(108, 99, 143, 0.8);
        }

        .result-field strong {
          color: rgba(108, 99, 143, 0.9);
          margin-right: 6px;
        }

        .result-error {
          background: rgba(244, 67, 54, 0.1);
          color: rgba(198, 40, 40, 0.9);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 12px;
          margin-top: 8px;
        }

        .batch-actions {
          display: flex;
          justify-content: center;
        }

        .finish-batch-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          border: none;
          padding: 14px 32px;
          border-radius: 25px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 6px 15px rgba(86, 171, 47, 0.3);
        }

        .finish-batch-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(86, 171, 47, 0.4);
        }

        /* 移動端優化 */
        @media (max-width: 768px) {
          .card-scanner-container {
            padding: 15px;
          }

          .button-container {
            flex-direction: column;
            gap: 10px;
            margin: 20px 0;
          }

          .custom-file-input {
            width: 100%;
            padding: 14px 28px;
            font-size: 16px;
            flex: 1;
          }

          .upload-button {
            width: 100%;
            padding: 12px 24px;
          }

          .image-preview,
          .parsed-data {
            padding: 16px;
            margin: 15px 0;
          }

          .card-image {
            max-height: 250px;
          }

          .note-textarea {
            min-height: 80px;
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default CardScanner;
